import { Suspense, useContext, useEffect, useMemo } from 'react';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import {
    AccessoriesPageData,
    AccessoriesPageVariables,
    getAccessoriesPageConfig,
} from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { skipToken } from '@apollo/client';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { AccessoriesPage } from 'src/modules/accessories/AccessoriesPage.tsx';
import { useUserInputState } from '../user-input/useUserInputState';

export const PrefetchAccessories = () => {
    const {
        valveLabel,
        actuatorLabel,
        page,
    } = useParams();

    if (!valveLabel) {
        throw new Error('No Valve selected');
    }
    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }
    if (!page) {
        throw new Error('No page provided');
    }

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const navigate = useNavigate();

    const { locationState, setLocationState } = useUserInputState([]);

    useEffect(() => {
        if (locationState.valveLabel !== valveLabel) {
            setLocationState({ ...locationState, valveLabel });
        }
        if (locationState.actuatorLabel !== actuatorLabel) {
            setLocationState({ ...locationState, actuatorLabel });
        }
        if (valveLabel === actuatorLabel && locationState.valvesPage !== page) {
            setLocationState({ ...locationState, valvesPage: page });
        }
    }, [locationState, setLocationState, valveLabel, actuatorLabel, page]);

    const [
        searchParams,
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...pageConfig.actuatorsFilterConfig,
    ]);

    useCheckMainFilter({
        filter,
        searchParams,
        navigate,
        filterConfig: [],
    });

    const variables = useMemo((): {
        variables: AccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                actuatorLabel,
                valveLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        valveLabel,
        userSettings,
    ]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<AccessoriesPageData, AccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return (<SkeletonMainLayout/>);
                    }
                    return (
                        <AccessoriesPage
                            valveLabel={valveLabel}
                            actuatorLabel={actuatorLabel}
                            data={data}
                            filter={filter}
                            searchParams={searchParams}
                            page={page}
                        />
                    );
                }}
            </Query>
        </Suspense>
    );
};
